import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import DateStrip from '../components/DateStrip';
import { useAuth } from '../context/AuthContext';
import {
  getInterests,
  getLocalities,
  getChangemakers,
  getMyActivities,
  getMyReviews,
  markParticipation,
  getActivitiesFeed,
  getActivityCalendarDates,
} from '../api/api';
import { Calendar, MapPin, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, SlidersHorizontal, Check, Search, ArrowRight } from 'lucide-react';
import { InterestIcon } from '../components/admin/AdminInterests';
import { todayYMD, addDaysYMD, weekendYMDs } from '../utils/dates';
import { track, setCurrentPage } from '../utils/analytics';
import { toggleActivityInterest } from '../api/api';
import ActivityCard from '../components/ActivityCard';
import MiniCalendar from '../components/MiniCalendar';
import SEOHead from '../components/SEOHead';
import FilterLocationMap from '../components/FilterLocationMap';

// Prefix relative /api/... image paths with the backend base URL
const resolveImgUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const INITIAL_LIMIT = 30;
const PAGE_SIZE = 20;

const ActivitiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Filter state — initialised from URL params for back-navigation ─────────
  const [selectedDates, setSelectedDates] = useState(() => {
    const raw = searchParams.get('dates');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [selectedInterests, setSelectedInterests] = useState(() => {
    const raw = searchParams.get('interests');
    return raw ? raw.split(',').filter(Boolean) : [];
  });
  const [selectedLocation, setSelectedLocation] = useState(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const locName = searchParams.get('loc');
    if (lat && lng) return { lat: parseFloat(lat), lng: parseFloat(lng), name: locName || '' };
    return null;
  });
  const [distanceKm, setDistanceKm] = useState(() => {
    const r = searchParams.get('radius');
    return r ? parseFloat(r) : 10;
  });
  const [showOnline, setShowOnline] = useState(true);
  const [showAllInterests, setShowAllInterests] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterLockedRef = useRef(false);
  const [whenOpen, setWhenOpen] = useState(true);
  const [whatOpen, setWhatOpen] = useState(true);
  const [searchQuery, setSearchQuery]                 = useState('');
  const [executedSearchQuery, setExecutedSearchQuery] = useState('');
  const [searchActive, setSearchActive]               = useState(false);
  const [showSearchDrop, setShowSearchDrop]           = useState(false);
  const [whereOpen, setWhereOpen]                     = useState(true);
  // Desktop sidebar collapse state
  const [deskWhatOpen, setDeskWhatOpen]   = useState(true);
  const [deskWhereOpen, setDeskWhereOpen] = useState(true);
  const [deskWhenOpen, setDeskWhenOpen]   = useState(true);
  const searchBarRef = useRef(null);
  const { user, openAuthModal, loading } = useAuth();
  const queryClient = useQueryClient();
  const autoInterestDone = useRef(false);
  const scrollTimerRef = useRef(null);
  const feedRef = useRef(null);
  const maxScrollRef = useRef(0);
  const observerRef = useRef(null);
  const pendingScrollToRef = useRef(null);
  const debounceRef = useRef(null);
  const dropHoveredRef = useRef(false);
  const [emailInterestedId, setEmailInterestedId] = useState(null);
  const [loadedUpTo, setLoadedUpTo] = useState(2);

  // ── Server-side paginated feed state ──────────────────────────────────────
  const [feedActivities, setFeedActivities] = useState([]);
  const [feedTotal, setFeedTotal] = useState(0);
  const [isFeedLoading, setIsFeedLoading] = useState(true);
  const [isFeedLoadingMore, setIsFeedLoadingMore] = useState(false);
  const [isFeedError, setIsFeedError] = useState(false);
  const sentinelRef = useRef(null);
  const firstLoadDoneRef = useRef(false);

  // ── Search suggestions (populated from API results) ───────────────────────
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  useEffect(() => { setCurrentPage('activities'); track('page_view', { page: 'activities', active_filters: { dates: [], interests: [] } }); }, []);

  // Preload Google Maps on page load so the WHERE filter map is ready instantly
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    if (window.google?.maps?.Map) return;
    const existing = document.getElementById('gmap-script');
    if (existing) return; // already injecting
    const s = document.createElement('script');
    s.id = 'gmap-script';
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&v=weekly`;
    s.async = true;
    document.head.appendChild(s);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Signed-out tutorial: show "Click here to Join" tooltip after 4s ──────────
  const TUTORIAL_SS_KEY = 'karmana_activities_tutorial_seen';
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialVisibleIds, setTutorialVisibleIds] = useState(new Set());
  const [tutorialTimerFired, setTutorialTimerFired] = useState(false);
  const tutorialTimerRef = useRef(null);

  // Start (or cancel) the 4-second timer — works on direct load AND navigation from home
  useEffect(() => {
    if (tutorialTimerRef.current) clearTimeout(tutorialTimerRef.current);
    if (user || loading) return;
    try { if (sessionStorage.getItem(TUTORIAL_SS_KEY)) return; } catch {}
    tutorialTimerRef.current = setTimeout(() => {
      try { if (sessionStorage.getItem(TUTORIAL_SS_KEY)) return; } catch {}
      setTutorialTimerFired(true);
    }, 4000);
    return () => { if (tutorialTimerRef.current) clearTimeout(tutorialTimerRef.current); };
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissTutorial = useCallback(() => {
    setShowTutorial(false);
    setTutorialVisibleIds(new Set());
    setTutorialTimerFired(false);
    try { sessionStorage.setItem(TUTORIAL_SS_KEY, '1'); } catch {}
  }, []);

  // NOTE: the "find all visible buttons" effect is placed AFTER `displayed`
  // is declared below (to avoid TDZ reference error).

  // Auto-log email click when user arrives via an email link (utm_source=email)
  useEffect(() => {
    const utmSource = searchParams.get('utm_source');
    if (utmSource === 'email') {
      track('email_click', {
        utm_campaign: searchParams.get('utm_campaign') || null,
        utm_medium: searchParams.get('utm_medium') || null,
        email_log_id: searchParams.get('log_id') || null,
      });
    }
  }, []); // mount only — intentional

  // Detect arrival from email "Mark as Interested" link (adds from_email=1 param)
  useEffect(() => {
    const fromEmail = searchParams.get('from_email');
    const actId = searchParams.get('activity_id');
    if (fromEmail === '1' && actId) {
      setEmailInterestedId(actId);
      const next = new URLSearchParams(searchParams);
      next.delete('from_email');
      setSearchParams(next, { replace: true });
    }
  }, []); // mount only — intentional

  // Scroll depth tracking: fire page_scroll once per new max block seen
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!feedRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const idx = parseInt(entry.target.dataset.blockIndex, 10);
        if (idx > maxScrollRef.current) {
          maxScrollRef.current = idx;
          track('page_scroll', { blocks_scrolled: idx });
        }
      });
    }, { threshold: 0.3 });

    observerRef.current = observer;
    feedRef.current.querySelectorAll('[data-block-index]').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }); // no deps — re-runs after every render so new cards get observed

  // Step 1 (mount only): capture the scrollTo ID from the URL into a ref,
  // then immediately clean the param so it doesn't show in the address bar.
  // Using [] avoids the self-defeating loop where setSearchParams changes
  // searchParams, fires cleanup, and kills the poll before it finds the card.
  useEffect(() => {
    const scrollToId = searchParams.get('scrollTo');
    const next = new URLSearchParams(searchParams);
    if (scrollToId) {
      pendingScrollToRef.current = scrollToId;
      next.delete('scrollTo');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 2 (mount only): poll for the card element and scroll to it.
  // The ref set above is stable — no deps needed, and cleanup can't race with it.
  useEffect(() => {
    const scrollToId = pendingScrollToRef.current;
    if (!scrollToId) return;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const el = document.querySelector(`[data-testid="activity-card-${scrollToId}"]`);
      if (el) {
        clearInterval(poll);
        pendingScrollToRef.current = null;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Brief green ring to let the user know exactly which card they returned to
        el.classList.add('karmana-scroll-highlight');
        setTimeout(() => el.classList.remove('karmana-scroll-highlight'), 2100);
      } else if (attempts > 60) { // up to 6 seconds — enough for data to load
        clearInterval(poll);
      }
    }, 100);
    return () => clearInterval(poll);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture shared activity ID from URL (in case user lands on list page via app_share link)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('utm_source') === 'app_share' && p.get('activity_id')) {
      sessionStorage.setItem('karmana_shared_activity_id', p.get('activity_id'));
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (filterLockedRef.current) return;
      if (window.scrollY > 100) {
        if (!scrollTimerRef.current) {
          scrollTimerRef.current = setTimeout(() => {
            setFiltersOpen(false);
            scrollTimerRef.current = null;
          }, 2000);
        }
      } else {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, []);

  // ── URL → filter sync (keep URL in sync when filters change) ────────────
  const filterSyncInitRef = useRef(false);
  useEffect(() => {
    if (!filterSyncInitRef.current) { filterSyncInitRef.current = true; return; }
    const next = new URLSearchParams(searchParams);
    if (selectedInterests.length > 0) next.set('interests', selectedInterests.join(','));
    else next.delete('interests');
    if (selectedDates.length > 0) next.set('dates', selectedDates.join(','));
    else next.delete('dates');
    if (selectedLocation) {
      next.set('lat', selectedLocation.lat);
      next.set('lng', selectedLocation.lng);
      next.set('loc', selectedLocation.name || '');
      next.set('radius', distanceKm);
    } else {
      next.delete('lat'); next.delete('lng'); next.delete('loc'); next.delete('radius');
    }
    setSearchParams(next, { replace: true });
  }, [selectedInterests, selectedDates, selectedLocation, distanceKm]); // eslint-disable-line

  // ── Paginated feed fetch ──────────────────────────────────────────────────
  const activityCountTrackedRef = useRef(false);

  useEffect(() => {
    const doLoad = async () => {
      const sessionId = (() => { try { return sessionStorage.getItem('karmana_session_id'); } catch { return null; } })();
      const sharedActivityId = (() => { try { return sessionStorage.getItem('karmana_shared_activity_id'); } catch { return null; } })();

      // Back-navigation: restore loaded count on first mount if saved
      let initialLimit = INITIAL_LIMIT;
      let restoreScroll = false;
      let savedScrollY = 0;
      if (!firstLoadDoneRef.current) {
        firstLoadDoneRef.current = true;
        try {
          const savedCount = parseInt(sessionStorage.getItem('karmana_feed_loaded') || '0', 10);
          const savedScroll = parseInt(sessionStorage.getItem('karmana_activities_scroll') || '0', 10);
          if (savedCount > 0 && savedScroll > 0) {
            initialLimit = Math.max(savedCount, INITIAL_LIMIT);
            restoreScroll = true;
            savedScrollY = savedScroll;
            sessionStorage.removeItem('karmana_feed_loaded');
            sessionStorage.removeItem('karmana_activities_scroll');
          }
        } catch {}
      }

      setIsFeedLoading(true);
      setIsFeedError(false);
      setFeedActivities([]);
      try {
        const res = await getActivitiesFeed({
          offset: 0, limit: initialLimit,
          dates: selectedDates, interests: selectedInterests,
          lat: selectedLocation?.lat, lng: selectedLocation?.lng,
          radiusKm: distanceKm,
          search: executedSearchQuery,
          sessionId, sharedActivityId,
          showOnline,
        });
        const data = res.data;
        setFeedActivities(data.activities || []);
        setFeedTotal(data.total || 0);
        if (!activityCountTrackedRef.current) {
          activityCountTrackedRef.current = true;
          track('page_view', { page: 'activities', activity_count: data.total || 0 });
        }
        if (restoreScroll) {
          setTimeout(() => window.scrollTo({ top: savedScrollY, behavior: 'instant' }), 200);
        }
        if (executedSearchQuery.trim()) {
          setSearchSuggestions((data.activities || []).slice(0, 3).map(a => ({ id: a.id, slug: a.slug, name: a.name })));
        }
      } catch {
        setIsFeedError(true);
      } finally {
        setIsFeedLoading(false);
      }
    };
    doLoad();
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    JSON.stringify(selectedDates),
    JSON.stringify(selectedInterests),
    JSON.stringify(selectedLocation),
    distanceKm,
    executedSearchQuery,
    showOnline,
  ]);

  // ── Load more (infinite scroll) ───────────────────────────────────────────
  const loadMoreRef = useRef(null);
  loadMoreRef.current = async () => {
    if (isFeedLoadingMore || feedActivities.length >= feedTotal) return;
    const sessionId = (() => { try { return sessionStorage.getItem('karmana_session_id'); } catch { return null; } })();
    const sharedActivityId = (() => { try { return sessionStorage.getItem('karmana_shared_activity_id'); } catch { return null; } })();
    setIsFeedLoadingMore(true);
    try {
      const res = await getActivitiesFeed({
        offset: feedActivities.length, limit: PAGE_SIZE,
        dates: selectedDates, interests: selectedInterests,
        lat: selectedLocation?.lat, lng: selectedLocation?.lng,
        radiusKm: distanceKm,
        search: executedSearchQuery,
        sessionId, sharedActivityId,
        showOnline,
      });
      const data = res.data;
      setFeedActivities(prev => [...prev, ...(data.activities || [])]);
      setFeedTotal(data.total || 0);
    } catch {} finally {
      setIsFeedLoadingMore(false);
    }
  };

  // Observe sentinel 5 cards from end of list
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMoreRef.current?.(); },
      { threshold: 0.1 }
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [feedActivities.length]); // re-observe when list grows

  const { data: interests = [] } = useQuery({
    queryKey: ['interests'],
    queryFn: async () => (await getInterests()).data,
  });

  const { data: localities = [] } = useQuery({
    queryKey: ['localities'],
    queryFn: async () => (await getLocalities()).data,
  });

  const { data: changemakersData = [] } = useQuery({
    queryKey: ['changemakers'],
    queryFn: async () => (await getChangemakers()).data,
  });

  const getChangemakerById = (id) => changemakersData.find((cm) => cm.id === id);

  const isActivityPast = (activity) => {
    if (activity.is_ongoing) {
      if (!activity.date_end) return false; // no end date: never past
      return activity.date_end < todayYMD();
    }
    const endDate = activity.date_end || activity.date;
    if (!endDate) return false;
    return endDate < todayYMD();
  };

  const { data: myActs } = useQuery({
    queryKey: ['my-activities'],
    queryFn: async () => (await getMyActivities()).data,
    enabled: !!user,
  });

  const { data: myReviews = [] } = useQuery({
    queryKey: ['my-reviews'],
    queryFn: async () => (await getMyReviews()).data,
    enabled: !!user,
  });

  // Map activity_id → feedback status ('pending' | 'approved' | 'rejected') for cards that need it
  // Priority: approved > pending > rejected (handles case of rejected + re-submitted = pending)
  const FEEDBACK_PRIORITY = { approved: 3, pending: 2, rejected: 1 };
  const feedbackStatusMap = useMemo(() => {
    const map = {};
    myReviews.forEach(({ review }) => {
      if (review?.review_type === 'activity' && review?.activity_id) {
        const status = review.status || 'pending';
        const current = map[review.activity_id];
        if (!current || (FEEDBACK_PRIORITY[status] || 0) > (FEEDBACK_PRIORITY[current] || 0)) {
          map[review.activity_id] = status;
        }
      }
    });
    return map;
  }, [myReviews]);

  const interestedIds = useMemo(() => {
    const all = [...(myActs?.upcoming || []), ...(myActs?.past || [])];
    // All returned records are non-cancelled; server already filters out cancelled
    return new Set(all.map((e) => e.activity.id));
  }, [myActs]);

  // Registration completed: derived from the registration_completed field added to my_activities
  const registeredIds = useMemo(() => {
    const all = [...(myActs?.upcoming || []), ...(myActs?.past || [])];
    return new Set(all.filter((e) => e.registration_completed).map((e) => e.activity.id));
  }, [myActs]);

  const attendedIds = useMemo(() => {
    const all = [...(myActs?.upcoming || []), ...(myActs?.past || [])];
    return new Set(all.filter((e) => e.interest?.participated === true).map((e) => e.activity.id));
  }, [myActs]);

  const handleRegistered = (activityId) => {
    queryClient.invalidateQueries({ queryKey: ['my-activities'] });
    if (activityId) queryClient.invalidateQueries({ queryKey: ['engagement', activityId] });
  };

  const handleInterest = async (activityId, changemakerId, cardCtx = {}) => {
    const act = feedActivities.find((a) => a.id === activityId);

    if (!user) {
      track('signup_trigger', { trigger: 'activity_card_interested', page: 'activities', activity_id: activityId, changemaker_id: changemakerId || null, ...cardCtx });
      localStorage.setItem('karmana_pending_interest', JSON.stringify({ activityId, returnPath: window.location.pathname }));
      openAuthModal('interested');
      return;
    }

    try {
      const r = await toggleActivityInterest(activityId);
      if (r.data && r.data.interested) {
        queryClient.setQueryData(['engagement', activityId], r.data);

        const actFromFeed = feedActivities.find((a) => a.id === activityId);
        if (actFromFeed) {
          queryClient.setQueryData(['my-activities'], (old) => {
            const cur = old || { upcoming: [], past: [] };
            const existingIds = new Set([
              ...(cur.upcoming || []).map((e) => e.activity.id),
              ...(cur.past  || []).map((e) => e.activity.id),
            ]);
            if (existingIds.has(activityId)) {
              // Already in list — just update registration_completed
              const patch = (list) => list.map((e) =>
                e.activity.id === activityId
                  ? { ...e, registration_completed: r.data.registration_completed || false }
                  : e
              );
              return { upcoming: patch(cur.upcoming || []), past: patch(cur.past || []) };
            }
            const entry = {
              activity: actFromFeed,
              interest: { status: 'interested', participated: null },
              registration_completed: r.data.registration_completed || false,
            };
            const todayStr = new Date().toISOString().split('T')[0];
            const actDate = act.date_end || act.date;
            if (!actDate || actDate >= todayStr) {
              return { ...cur, upcoming: [...(cur.upcoming || []), entry] };
            }
            return { ...cur, past: [...(cur.past || []), entry] };
          });
        }

        // 3. Background sync — fire-and-forget (don't await)
        queryClient.invalidateQueries({ queryKey: ['my-activities'] });
      }
    } catch (err) {
      throw err;
    }
  };

  const handleAttend = (activityId, value) => {
    if (!user) { openAuthModal('attended'); return; }
    markParticipation(activityId, value)
      .then(() => queryClient.invalidateQueries({ queryKey: ['my-activities'] }))
      .catch(() => {});
  };

  useEffect(() => {
    const autoInterestId = searchParams.get('autoInterest');
    if (!autoInterestId || !user || loading || autoInterestDone.current) return;
    if (myActs === undefined) return;
    autoInterestDone.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete('autoInterest');
    setSearchParams(next, { replace: true });
    localStorage.removeItem('karmana_pending_interest');
    const all = [...(myActs?.upcoming || []), ...(myActs?.past || [])];
    const alreadyInterested = all.some((e) => e.activity.id === autoInterestId);
    if (!alreadyInterested) {
      toggleActivityInterest(autoInterestId)
        .then(() => {
          track('post_signup_action_completed', { page: 'activities', activity_id: autoInterestId });
          queryClient.invalidateQueries({ queryKey: ['my-activities'] });
          queryClient.invalidateQueries({ queryKey: ['engagement', autoInterestId] });
        })
        .catch(() => {});
    }
  }, [user, loading, myActs, searchParams]);

  const topInterests = useMemo(() => interests.filter((i) => i.parent_id), [interests]);

  // Save search query to analytics and commit the search
  const commitSearch = useCallback((q, closeDrop = true) => {
    const trimmed = q.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setExecutedSearchQuery(trimmed);
    if (closeDrop) setShowSearchDrop(false);
    setFiltersOpen(false);
    if (!trimmed) return;
    // Reset filters — search and filters are mutually exclusive
    setSelectedDates([]);
    setSelectedInterests([]);
    setSelectedLocation(null);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    let sessionId = null;
    try { sessionId = sessionStorage.getItem('karmana_session_id'); } catch {}
    fetch(`${API_URL}/api/analytics/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: trimmed, user_id: user?.id || null, session_id: sessionId, source: 'activities_page' }),
    }).catch(() => {});
    track('activities_search_submit', { query: trimmed });
  }, [user]); // eslint-disable-line

  const handleSearch = useCallback(() => {
    commitSearch(searchQuery);
  }, [searchQuery, commitSearch]);

  const handleQueryChange = (val) => {
    setSearchQuery(val);
    setShowSearchDrop(val.length >= 1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => commitSearch(val, false), 400);
  };

  // After timer fires + DOM is ready: find ALL visible non-past join buttons in the viewport
  useEffect(() => {
    if (!tutorialTimerFired || showTutorial) return;
    const t = todayYMD();
    const isPastFn = (a) => {
      if (a.is_ongoing) return a.date_end ? a.date_end < t : false;
      const end = a.date_end || a.date;
      return end ? end < t : false;
    };
    const nonPastIds = new Set(feedActivities.filter((a) => !isPastFn(a)).map((a) => a.id));
    if (nonPastIds.size === 0) return;

    const vh = window.innerHeight;
    const visibleIds = [];

    document.querySelectorAll('[data-testid^="join-btn-"]').forEach((btn) => {
      const id = btn.getAttribute('data-testid').replace('join-btn-', '');
      if (!nonPastIds.has(id)) return;
      const rect = btn.getBoundingClientRect();
      // Partially or fully visible in the viewport
      if (rect.top < vh && rect.bottom > 0) {
        visibleIds.push(id);
      }
    });

    if (visibleIds.length > 0) {
      setTutorialVisibleIds(new Set(visibleIds));
      setShowTutorial(true);
    }
  }, [tutorialTimerFired, feedActivities]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = todayYMD();
  const tomorrow = addDaysYMD(today, 1);
  const weekend = weekendYMDs();

  const ymdToLabel = (ymd) => {
    try {
      const d = new Date(`${ymd}T00:00:00`);
      const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${m[d.getMonth()]} ${d.getDate()}`;
    } catch { return ymd; }
  };
  const clearSearch = () => { setExecutedSearchQuery(''); setSearchQuery(''); };

  // ── URL ?q= sync ───────────────────────────────────────────────────────────
  // On mount: restore search query from URL (browser Back support)
  const searchRestoredRef = useRef(false);
  useEffect(() => {
    if (searchRestoredRef.current) return;
    searchRestoredRef.current = true;
    const q = searchParams.get('q');
    if (q) { setSearchQuery(q); setExecutedSearchQuery(q); }
  }, []); // eslint-disable-line

  // Keep ?q= in sync with executedSearchQuery
  useEffect(() => {
    if (!searchRestoredRef.current) return;
    const next = new URLSearchParams(searchParams);
    if (executedSearchQuery.trim()) next.set('q', executedSearchQuery.trim());
    else next.delete('q');
    setSearchParams(next, { replace: true });
  }, [executedSearchQuery]); // eslint-disable-line

  // ── Scroll position save / restore ────────────────────────────────────────
  useEffect(() => {
    const q = searchParams.get('q');
    if (!q) return;
    const saved = sessionStorage.getItem('karmana_activities_scroll');
    if (!saved) return;
    const pos = parseInt(saved, 10);
    // Retry until we can actually scroll to the saved position (list may still be rendering)
    let attempts = 0;
    const tryScroll = () => {
      window.scrollTo({ top: pos, behavior: 'instant' });
      attempts++;
      if (attempts < 5 && Math.abs(window.scrollY - pos) > 50) {
        setTimeout(tryScroll, 150);
      } else {
        try { sessionStorage.removeItem('karmana_activities_scroll'); } catch {}
      }
    };
    const t = setTimeout(tryScroll, 200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  useEffect(() => () => {
    // Save scroll + loaded count when navigating away (for back-navigation restoration)
    try {
      sessionStorage.setItem('karmana_activities_scroll', String(window.scrollY));
      sessionStorage.setItem('karmana_feed_loaded', String(feedActivities.length));
    } catch {}
  }, [feedActivities.length]);

  const toggleDate = (ymd) => {
    const action = selectedDates.includes(ymd) ? 'deselect' : 'select';
    const newDates = selectedDates.includes(ymd) ? selectedDates.filter(d => d !== ymd) : [...selectedDates, ymd];
    track('filter_date_click', { date: ymd, action, active_dates: newDates, pill_label: ymdToLabel(ymd) });
    if (newDates.length > 0) clearSearch();
    setSelectedDates(newDates);
  };

  // Multi-select WHEN: toggle date group in/out
  const toggleQuick = (dates, label = '') => {
    const allIn = dates.every(d => selectedDates.includes(d));
    const newDates = allIn ? selectedDates.filter(d => !dates.includes(d)) : [...new Set([...selectedDates, ...dates])];
    track('filter_date_click', { date: dates.length === 1 ? dates[0] : null, action: allIn ? 'deselect' : 'select', active_dates: newDates, pill_label: label || (dates.length === 1 ? ymdToLabel(dates[0]) : null) });
    if (newDates.length > 0) clearSearch();
    setSelectedDates(newDates);
  };
  const isQuickDateActive = (dates) => dates.length > 0 && dates.every(d => selectedDates.includes(d));

  // Multi-select WHAT
  const toggleInterest = (id) => {
    const action = selectedInterests.includes(id) ? 'deselect' : 'select';
    const newInterests = selectedInterests.includes(id) ? selectedInterests.filter(x => x !== id) : [...selectedInterests, id];
    const interestObj = interests.find(i => i.id === id);
    track('filter_interest_click', { interest_id: id, interest_name: interestObj?.name || null, action, active_interests: newInterests });
    if (newInterests.length > 0) clearSearch();
    setSelectedInterests(newInterests);
  };

  const quickBtn = (label, dates, testid) => {
    const active = isQuickDateActive(dates);
    return (
      <button
        onClick={() => toggleQuick(dates, label)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap flex-shrink-0 ${
          active ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
        data-testid={testid}
      >
        <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors ${active ? 'bg-white border-white' : 'border-gray-400'}`}>
          {active && <Check size={9} className="text-green-600" />}
        </span>
        {label}
      </button>
    );
  };

  const filterIconBtn = (
    <button
      onClick={() => {
        if (filtersOpen) {
          filterLockedRef.current = false;
          setFiltersOpen(false);
          track('filter_panel_close', { active_filters: { dates: selectedDates, interests: selectedInterests }, filter_count: selectedDates.length + selectedInterests.length });
        } else {
          filterLockedRef.current = true;
          setFiltersOpen(true);
          setWhenOpen(true);
          setWhatOpen(true);
          track('filter_panel_open', { page: 'activities' });
        }
      }}
      className="p-2 -ml-1 relative transition-colors"
      data-testid="filter-icon-btn"
    >
      <SlidersHorizontal size={20} className={filtersOpen || selectedDates.length > 0 || selectedInterests.length > 0 || selectedLocation ? 'text-green-600' : 'text-gray-500'} />
      {(selectedDates.length > 0 || selectedInterests.length > 0 || selectedLocation) && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-green-500 rounded-full border border-white" />
      )}
    </button>
  );

  const searchBarCenter = (
    <div ref={searchBarRef} className="relative flex items-center w-full">
      {!searchActive ? (
        /* Idle: lens + placeholder + optional X to clear */
        <button
          onClick={() => { setSearchActive(true); setFiltersOpen(false); setShowSearchDrop(searchQuery.length >= 1); track('activities_search_open', {}); }}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-full border border-gray-200 hover:border-teal-300 bg-white transition-colors cursor-pointer"
          data-testid="activities-search-bar-idle"
        >
          <Search size={14} className="text-gray-400 flex-shrink-0" />
          <span className="flex-1 text-left text-[12px] text-gray-400 truncate">
            {executedSearchQuery || 'Search activities...'}
          </span>
          {executedSearchQuery && (
            <span
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); clearSearch(); }}
              className="p-0.5 rounded-full hover:bg-gray-100 transition-colors"
              data-testid="search-clear-idle"
            >
              <X size={12} className="text-gray-400" />
            </span>
          )}
        </button>
      ) : (
        /* Active: input + X + submit arrow */
        <div className="flex items-center w-full px-3 py-2 rounded-full border border-teal-400 ring-1 ring-teal-200 bg-white min-w-0">
          <input
            autoFocus
            type="text"
            value={searchQuery}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.currentTarget.blur(); handleSearch(); }
              if (e.key === 'Escape') { setSearchActive(false); setShowSearchDrop(false); }
            }}
            onBlur={() => {
              setTimeout(() => {
                if (dropHoveredRef.current) return; // user is interacting with dropdown
                setShowSearchDrop(false);
                setSearchActive(false);
                if (!searchQuery.trim()) clearSearch();
              }, 200);
            }}
            placeholder="Search activities..."
            className="flex-1 text-[12px] bg-transparent outline-none text-gray-700 placeholder-gray-400 min-w-0"
            data-testid="activities-search-input"
          />
          {(searchQuery || executedSearchQuery) && (
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { clearSearch(); setSearchActive(false); }}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 ml-1"
              data-testid="search-clear-active"
            >
              <X size={14} />
            </button>
          )}
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.currentTarget.closest('div')?.querySelector('input')?.blur(); handleSearch(); }}
            className="text-teal-600 flex-shrink-0 ml-1"
            data-testid="activities-search-submit"
          >
            <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Layout mobileHeaderLeft={filterIconBtn} headerCenter={searchBarCenter}>
      <SEOHead
        title="Sustainability Events & Activities in Bengaluru"
        description="Discover upcoming waste management, circular economy, and social impact activities in Bengaluru. Join the community making a difference."
        canonical="https://karmana.in/activities"
      />

      {/* ── Search suggestions dropdown (mobile + desktop) ── */}
      {showSearchDrop && searchSuggestions.length > 0 && (
        <div
          className="fixed top-14 md:top-16 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-[480px] z-[51] bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          data-testid="search-suggestions-dropdown"
          onMouseEnter={() => { dropHoveredRef.current = true; }}
          onMouseLeave={() => { dropHoveredRef.current = false; }}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={() => { dropHoveredRef.current = true; }}
        >
          {searchSuggestions.map((s) => (
            <button
              key={s.id}
              onClick={() => { setSearchQuery(s.name); setShowSearchDrop(false); navigate(`/activities/${s.slug || s.id}`); track('activities_search_suggestion_click', { activity_id: s.id, activity_name: s.name }); }}
              className="flex items-center justify-between w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              data-testid={`search-suggestion-${s.id}`}
            >
              <span>{s.name}</span>
              <ArrowRight size={13} className="text-gray-400 flex-shrink-0 ml-2" />
            </button>
          ))}
        </div>
      )}

      {/* Mobile filter panel — fixed so it floats above the hero and activity cards when open */}
      <div className={`md:hidden fixed top-14 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${filtersOpen ? 'shadow-lg border-b border-gray-200' : 'pointer-events-none'}`}>
        <div className={`transition-all duration-300 ${filtersOpen ? 'max-h-[85vh] overflow-y-auto' : 'max-h-0 overflow-hidden'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">

          {/* ── WHAT section ── */}
          {topInterests.length > 0 && (
            <div className="mb-0">
              <button
                onClick={() => { track('filter_accordion_toggle', { section: 'what', action: whatOpen ? 'close' : 'open' }); setWhatOpen((p) => !p); }}
                className="flex items-center gap-1 text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5"
                data-testid="filter-what-toggle"
              >
                What
                <ChevronDown size={11} className={`transition-transform duration-200 ${whatOpen ? '' : '-rotate-90'}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${whatOpen ? 'max-h-[80px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
                <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
                  {topInterests.map((interest) => {
                    const active = selectedInterests.includes(interest.id);
                    return (
                      <button
                        key={interest.id}
                        onClick={() => toggleInterest(interest.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap flex-shrink-0 ${
                          active ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`quick-interest-${interest.id}`}
                      >
                        <InterestIcon icon={interest.icon} size={14} className={active ? 'text-white' : 'text-green-600'} />
                        {interest.name}
                      </button>
                    );
                  })}
                  {selectedInterests.length > 0 && (
                    <button
                      onClick={() => setSelectedInterests([])}
                      className="flex items-center gap-1 px-3 py-2 rounded-full text-sm text-gray-500 hover:text-gray-800 whitespace-nowrap flex-shrink-0"
                      data-testid="quick-clear-what"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Divider ── */}
          <div className="h-px bg-gray-100 my-2" />

          {/* ── WHERE section ── */}
          <div className="mb-0">
            <button
              onClick={() => { track('filter_accordion_toggle', { section: 'where', action: whereOpen ? 'close' : 'open' }); setWhereOpen((p) => !p); }}
              className="flex items-center gap-1.5 w-full text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5"
              data-testid="filter-where-toggle"
            >
              Where
              <ChevronDown size={11} className={`transition-transform duration-200 ${whereOpen ? '' : '-rotate-90'}`} />
              {selectedLocation && <span className="ml-1 text-[9px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded-full">Active</span>}
            </button>
            <div className={`transition-all duration-300 ${whereOpen ? 'max-h-[290px] overflow-visible opacity-100' : 'max-h-0 overflow-hidden opacity-0 pointer-events-none'}`}>
              <FilterLocationMap
                value={selectedLocation}
                onChange={(loc) => {
                  setSelectedLocation(loc);
                  if (loc) { clearSearch(); track('filter_location_select', { location_name: loc.name, is_auto_detect: loc.isAutoDetect || false }); }
                  else track('filter_location_clear', {});
                }}
                userId={user?.id || null}
                sessionId={(() => { try { return sessionStorage.getItem('karmana_session_id'); } catch { return null; } })()}
                distanceKm={distanceKm}
                onDistanceChange={setDistanceKm}
                showOnline={showOnline}
                onShowOnlineChange={setShowOnline}
              />
            </div>
          </div>

          {/* ── Divider ── */}
          <div className="h-px bg-gray-100 my-2" />

          {/* ── WHEN section (dates only) ── */}
          <div className="mb-0">
            <button
              onClick={() => { track('filter_accordion_toggle', { section: 'when', action: whenOpen ? 'close' : 'open' }); setWhenOpen((p) => !p); }}
              className="flex items-center gap-1 text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5"
              data-testid="filter-when-toggle"
            >
              When
              <ChevronDown size={11} className={`transition-transform duration-200 ${whenOpen ? '' : '-rotate-90'}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${whenOpen ? 'max-h-[200px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
              {selectedDates.length > 0 && (
                <button
                  onClick={() => setSelectedDates([])}
                  className="flex items-center gap-1 px-3 py-1.5 mb-1.5 rounded-full text-xs text-gray-500 hover:text-gray-800 border border-gray-200 whitespace-nowrap"
                  data-testid="quick-clear-when"
                >
                  <X size={12} /> Clear dates
                </button>
              )}
              <DateStrip selectedDates={selectedDates} onSelectDate={toggleDate} color="green" />
            </div>
          </div>

          {/* ── View X Activities + Clear Filters + Close row ── */}
          <div className="h-px bg-gray-100 mt-2 mb-2.5" />
          <div className="flex items-center gap-2 pb-1 flex-wrap">
            <button
              onClick={() => { filterLockedRef.current = false; setFiltersOpen(false); track('filter_panel_close', { active_filters: { dates: selectedDates, interests: selectedInterests }, filter_count: selectedDates.length + selectedInterests.length + (selectedLocation ? 1 : 0) }); }}
              data-testid="view-activities-btn"
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-green-600 text-white border border-green-600 whitespace-nowrap overflow-hidden active:scale-95 transition-transform"
            >
              View {feedTotal} {feedTotal === 1 ? 'Activity' : 'Activities'}
            </button>
            {(selectedDates.length > 0 || selectedInterests.length > 0 || selectedLocation) && (
              <button
                onClick={() => { setSelectedDates([]); setSelectedInterests([]); setSelectedLocation(null); }}
                data-testid="clear-filters-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-500 border border-gray-300 whitespace-nowrap overflow-hidden active:scale-95 transition-transform hover:bg-gray-50"
              >
                <X size={13} />
                Clear Filters
              </button>
            )}
            <button
              onClick={() => { filterLockedRef.current = false; setFiltersOpen(false); track('filter_panel_close', { active_filters: { dates: selectedDates, interests: selectedInterests }, filter_count: selectedDates.length + selectedInterests.length + (selectedLocation ? 1 : 0) }); }}
              data-testid="filter-close-btn"
              className="ml-auto inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-600 border border-gray-300 whitespace-nowrap overflow-hidden active:scale-95 transition-transform hover:bg-gray-50"
            >
              Close
            </button>
          </div>

        </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-0 md:py-8">
        <div className="md:flex md:gap-10 md:items-start">

          {/* ── DESKTOP ONLY: Left sidebar filters (sticky) ── */}
          <div className="hidden md:block w-64 flex-shrink-0 sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto pb-8">

            {/* WHAT */}
            {topInterests.length > 0 && (
              <div className="mb-4">
                <button
                  className="w-full flex items-center justify-between mb-2 group"
                  onClick={() => setDeskWhatOpen((p) => !p)}
                >
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">What</p>
                  <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${deskWhatOpen ? '' : '-rotate-90'}`} />
                </button>
                {deskWhatOpen && (
                  <>
                    <div className={`flex flex-wrap gap-1.5 ${showAllInterests ? 'max-h-44 overflow-y-auto pr-0.5' : ''}`}>
                      {(showAllInterests ? topInterests : topInterests.slice(0, 5)).map((interest) => {
                        const active = selectedInterests.includes(interest.id);
                        return (
                          <button
                            key={interest.id}
                            onClick={() => toggleInterest(interest.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              active ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                            }`}
                            data-testid={`sidebar-interest-${interest.id}`}
                          >
                            <InterestIcon icon={interest.icon} size={11} className={active ? 'text-white' : 'text-green-600'} />
                            {interest.name}
                          </button>
                        );
                      })}
                      {selectedInterests.length > 0 && (
                        <button
                          onClick={() => setSelectedInterests([])}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs text-gray-500 hover:text-red-500 border border-gray-200 transition-colors"
                          data-testid="sidebar-clear-what"
                        >
                          <X size={12} /> Clear
                        </button>
                      )}
                    </div>
                    {topInterests.length > 5 && (
                      <button
                        onClick={() => setShowAllInterests((v) => !v)}
                        className="mt-2 text-xs text-teal-600 hover:text-teal-800 font-medium"
                        data-testid="sidebar-show-more-interests"
                      >
                        {showAllInterests ? 'Show less' : 'Show more'}
                      </button>
                    )}
                  </>
                )}
              </div>
            )}

            {/* WHERE */}
            <div className="h-px bg-gray-100 mb-3" />
            <div className="mb-4">
              <button
                className="w-full flex items-center justify-between mb-2 group"
                onClick={() => setDeskWhereOpen((p) => !p)}
              >
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Where</p>
                <div className="flex items-center gap-2">
                  {selectedLocation && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setSelectedLocation(null); track('filter_location_clear', {}); }}
                      className="text-[10px] text-red-400 hover:text-red-600 transition-colors cursor-pointer"
                      data-testid="sidebar-clear-where"
                    >
                      Clear
                    </span>
                  )}
                  <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${deskWhereOpen ? '' : '-rotate-90'}`} />
                </div>
              </button>
              {deskWhereOpen && (
                <FilterLocationMap
                  value={selectedLocation}
                  onChange={(loc) => {
                    setSelectedLocation(loc);
                    if (loc) { clearSearch(); track('filter_location_select', { location_name: loc.name, is_auto_detect: loc.isAutoDetect || false }); }
                    else track('filter_location_clear', {});
                  }}
                  userId={user?.id || null}
                  sessionId={(() => { try { return sessionStorage.getItem('karmana_session_id'); } catch { return null; } })()}
                  distanceKm={distanceKm}
                  onDistanceChange={setDistanceKm}
                  showOnline={showOnline}
                  onShowOnlineChange={setShowOnline}
                />
              )}
            </div>

            {/* WHEN */}
            <div className="h-px bg-gray-100 mb-3" />
            <div className="mb-6">
              <button
                className="w-full flex items-center justify-between mb-2 group"
                onClick={() => setDeskWhenOpen((p) => !p)}
              >
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">When</p>
                <div className="flex items-center gap-2">
                  {selectedDates.length > 0 && (
                    <span
                      onClick={(e) => { e.stopPropagation(); setSelectedDates([]); }}
                      className="text-[10px] text-red-400 hover:text-red-600 transition-colors cursor-pointer flex items-center gap-0.5"
                      data-testid="sidebar-clear-when"
                    >
                      <X size={10} /> Clear
                    </span>
                  )}
                  <ChevronDown size={12} className={`text-gray-400 transition-transform duration-200 ${deskWhenOpen ? '' : '-rotate-90'}`} />
                </div>
              </button>
              {deskWhenOpen && (
                <MiniCalendar selectedDates={selectedDates} onToggleDate={toggleDate} activityDates={[]} />
              )}
            </div>

          </div>

          {/* ── Cards column ── */}
          <div className="flex-1 min-w-0">
        {isFeedLoading ? (
          /* Initial fetch — skeleton cards */
          <div className="-mx-4 md:mx-auto md:max-w-[624px] space-y-4 md:space-y-5">
            {[1, 2, 3].map((n) => (
              <div key={n} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="h-44 bg-gray-100" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-gray-100 rounded w-3/4" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                  <div className="h-4 bg-gray-100 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : isFeedError ? (
          /* API failed */
          <div className="text-center py-16" data-testid="activities-error-state">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">Unable to load activities</p>
            <p className="text-gray-400 text-sm mb-5">Check your connection and try again</p>
            <button
              onClick={() => { setIsFeedError(false); setIsFeedLoading(true); }}
              className="px-5 py-2 bg-green-600 text-white rounded-full text-sm font-semibold hover:bg-green-700 transition-colors"
              data-testid="activities-retry-btn"
            >
              Retry
            </button>
          </div>
        ) : feedActivities.length === 0 ? (
          /* Data loaded — zero results */
          <div className="text-center py-16" data-testid="activities-empty-state">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            {executedSearchQuery.trim() ? (
              <p className="text-gray-600">
                No results found.{' '}
                <button onClick={clearSearch} className="text-teal-600 hover:underline font-medium" data-testid="see-all-activities-link">
                  See All Activities
                </button>
              </p>
            ) : (
              <p className="text-gray-600">No activities match your current filters.</p>
            )}
          </div>
        ) : (
          <>
            {/* ── TOP RESULTS — compact image cards, shown only when search is active ── */}
            {executedSearchQuery.trim() && (
              <div className="md:mx-auto md:max-w-[624px] mb-5 md:mb-6">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 px-4 md:px-0" data-testid="top-results-heading">Top Results</p>
                <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-4 md:mx-0 px-4 md:px-0 pb-1">
                  {feedActivities.slice(0, 3).map((activity, idx) => {
                    const organizers = [
                      getChangemakerById(activity.changemaker_id),
                      ...(activity.organizer_ids || []).map(getChangemakerById),
                    ].filter(Boolean);
                    const imgSrc = resolveImgUrl(
                      (activity.media_urls && activity.media_urls[0]) || activity.image_url
                    );
                    return (
                      <button
                        key={activity.id}
                        onClick={() => { navigate(`/activities/${activity.slug || activity.id}`); track('top_result_click', { activity_id: activity.id, activity_name: activity.name, position: idx }); }}
                        className="flex-shrink-0 w-44 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow text-left"
                        data-testid={`top-result-${activity.id}`}
                      >
                        <div className="h-28 overflow-hidden bg-gray-100 relative">
                          {imgSrc && (
                            <img src={imgSrc} alt={activity.name} className="w-full h-full object-cover" loading="lazy" />
                          )}
                          {isActivityPast(activity) && (
                            <div className="absolute inset-0 bg-gray-900/20 flex items-center justify-center">
                              <span className="text-white text-[10px] font-semibold bg-gray-700/70 px-2 py-0.5 rounded-full">Past</span>
                            </div>
                          )}
                        </div>
                        <div className="p-2.5 border border-t-0 border-gray-100 rounded-b-xl">
                          <p className="text-[12px] font-semibold text-gray-800 line-clamp-2 leading-tight mb-1">{activity.name}</p>
                          {activity.date && <p className="text-[10px] text-teal-600">{ymdToLabel(activity.date)}</p>}
                          {organizers[0] && <p className="text-[10px] text-gray-400 truncate mt-0.5">{organizers[0].name}</p>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mt-5 mb-3 px-4 md:px-0" data-testid="all-results-heading">All Results</p>
              </div>
            )}

            {/* ── MAIN FEED ── */}
            <div ref={feedRef} className="-mx-4 md:mx-auto md:max-w-[624px] space-y-4 md:space-y-5">
              {feedActivities.map((activity, idx) => {
                const organizers = [
                  getChangemakerById(activity.changemaker_id),
                  ...(activity.organizer_ids || []).map(getChangemakerById),
                ].filter(Boolean);
                // Sentinel 5 from end for infinite scroll trigger
                const isSentinel = idx === feedActivities.length - 5;

                return (
                  <div
                    key={activity.id}
                    ref={isSentinel ? sentinelRef : null}
                    data-block-index={Math.min(idx + 1, 10)}
                  >
                    <ActivityCard
                      activity={activity}
                      organizers={organizers}
                      otherName={activity.other_organizer_name || null}
                      isPast={isActivityPast(activity)}
                      isInterested={interestedIds.has(activity.id)}
                      onInterest={handleInterest}
                      isAttended={attendedIds.has(activity.id)}
                      onAttend={handleAttend}
                      isLoggedIn={!!user}
                      feedbackStatus={feedbackStatusMap[activity.id]}
                      interests={interests}
                      alreadyRegistered={registeredIds.has(activity.id)}
                      onRegistered={handleRegistered}
                      emailJustInterested={activity.id === emailInterestedId}
                      priority={idx < 3}
                      shouldLoad={idx <= loadedUpTo}
                      onVisible={() => setLoadedUpTo((prev) => Math.max(prev, idx + 3))}
                      activeFilters={{ dates: selectedDates, interests: selectedInterests }}
                      sourceProps={{
                        activity_id: activity.id,
                        activity_name: activity.name,
                        organisation_id: activity.changemaker_id || null,
                        organisation_name: organizers[0]?.name || null,
                        source_page: 'activities',
                        activity_position: idx,
                      }}
                      showCardTutorial={showTutorial && tutorialVisibleIds.has(activity.id)}
                      onTutorialDismiss={dismissTutorial}
                    />
                  </div>
                );
              })}
            </div>

            {/* Load-more shimmer */}
            {isFeedLoadingMore && (
              <div className="-mx-4 md:mx-auto md:max-w-[624px] mt-4 space-y-4">
                {[1, 2].map((n) => (
                  <div key={n} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                    <div className="h-44 bg-gray-100" />
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-gray-100 rounded w-3/4" />
                      <div className="h-4 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* End-of-feed indicator */}
            {!isFeedLoadingMore && feedActivities.length > 0 && feedActivities.length >= feedTotal && feedTotal > 0 && (
              <p className="text-center text-xs text-gray-400 py-6" data-testid="feed-end-indicator">
                All {feedTotal} {feedTotal === 1 ? 'activity' : 'activities'} shown
              </p>
            )}
          </>
        )}
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default ActivitiesPage;
